// ScanRoom — video-walkthrough or multi-photo capture for room inventory.
//
// Entry point from RoomDetail's inventory tile (📷 Scan Room). On confirm,
// hands off to the existing <RoomScan /> review screen via its
// initialReview prop, seeded with the v6 Edge Function response.
//
// State machine:
//   mode_select          — chooser (video / multi-photo)
//   recording            — live preview + timer (15-45s, stop disabled <15s)
//   capturing_photos     — shutter UI (1-6 photos)
//   extracting           — client-side frame extraction with progress
//   uploading_frames     — POST to enumerate-room-items (capture_mode=video|multi_photo)
//   review               — render <RoomScan initialReview={...} />
//   error                — terminal error with retry / close
//
// Casavio Tone Principle still applies. Copy describes what the product
// is doing; never characterizes the user, the room, or the captured frames.

import { useState, useRef, useEffect } from 'react'
import { Camera, Video as VideoIcon, X, Square, ArrowLeft } from 'lucide-react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'
import { uploadVideoInBackground } from '../utils/videoBackgroundUpload'
import RoomScan from './RoomScan'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const FRAME_INTERVAL_SEC = 2
const SKIP_INITIAL_MS = 500
const MIN_VIDEO_SEC = 15
const MAX_VIDEO_SEC = 45
const WARN_VIDEO_SEC = 40
const MAX_FRAMES = 23
const MULTI_PHOTO_MAX = 6
const MULTI_PHOTO_MIN = 1

const SPIN_STYLE = `@keyframes scanroom-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

function Spinner({ size = 24, color = C.amber }) {
  return (
    <div style={{
      width: `${size}px`, height: `${size}px`,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'scanroom-spin 0.9s linear infinite',
    }} />
  )
}

function formatMMSS(s) {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${String(sec).padStart(2, '0')}`
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: C.bg, zIndex: 1500,
  display: 'flex', flexDirection: 'column',
}
const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
  background: C.card, flexShrink: 0,
}
const headerTitleStyle = {
  fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown,
}
const closeBtnStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: C.muted, padding: '6px', borderRadius: '8px', lineHeight: 0,
}

export default function ScanRoom({ room, homeId, onClose, onSaved }) {
  const [mode, setMode] = useState('mode_select')
  const [errorMessage, setErrorMessage] = useState(null)

  // Recording state.
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [videoBlob, setVideoBlob] = useState(null)
  const [activeStream, setActiveStream] = useState(null)
  const streamRef = useRef(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const timerRef = useRef(null)
  const videoRef = useRef(null)

  // Multi-photo state.
  const [photoQueue, setPhotoQueue] = useState([]) // { id, file, previewUrl }[]
  const photoInputRef = useRef(null)
  const photoIdCounterRef = useRef(0)

  // Extraction progress (video frames only).
  const [extractProgress, setExtractProgress] = useState(0)
  const [extractTotal, setExtractTotal] = useState(0)

  // Result handed off to RoomScan review screen.
  const [scanResponse, setScanResponse] = useState(null)

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopRecordingTimers()
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
      photoQueue.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Attach stream to <video> element when both are available.
  useEffect(() => {
    if (videoRef.current && activeStream) {
      videoRef.current.srcObject = activeStream
    }
  }, [activeStream])

  // Background upload of the raw walkthrough video. Fires once when the
  // review screen takes over — RoomScan is already interactive at this
  // point, so this is fire-and-forget. The util handles retries + the
  // localStorage fallback.
  const bgUploadStartedRef = useRef(false)
  useEffect(() => {
    if (mode !== 'review' || !scanResponse?.session_id || !videoBlob) return
    if (bgUploadStartedRef.current) return
    bgUploadStartedRef.current = true
    ;(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        const userId = user?.id
        if (!userId) {
          console.error('[ScanRoom] background upload: no user id')
          return
        }
        uploadVideoInBackground(videoBlob, scanResponse.session_id, userId)
      } catch (err) {
        console.error('[ScanRoom] background upload kickoff failed', err)
      }
    })()
  }, [mode, scanResponse, videoBlob])

  // -- Helpers --------------------------------------------------------------

  function stopRecordingTimers() {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  function teardownStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    setActiveStream(null)
  }

  function handleClose() {
    if (mode === 'extracting' || mode === 'uploading_frames') return // no cancel mid-flight
    stopRecordingTimers()
    teardownStream()
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      try { recorderRef.current.stop() } catch { /* noop */ }
    }
    onClose()
  }

  // -- Video recording ------------------------------------------------------

  async function startRecording() {
    setErrorMessage(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      })
      streamRef.current = stream
      setActiveStream(stream)

      const mimeType = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : 'video/webm'
      const recorder = new MediaRecorder(stream, { mimeType })
      const chunks = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType })
        setVideoBlob(blob)
        teardownStream()
        setMode('extracting')
        extractAndProcess(blob)
      }
      recorder.start()
      recorderRef.current = recorder
      chunksRef.current = chunks
      setRecordingSeconds(0)
      setMode('recording')

      timerRef.current = setInterval(() => {
        setRecordingSeconds(s => {
          const next = s + 1
          if (next >= MAX_VIDEO_SEC) {
            stopRecordingTimers()
            try { recorder.stop() } catch { /* noop */ }
            return MAX_VIDEO_SEC
          }
          return next
        })
      }, 1000)
    } catch (err) {
      console.error('[ScanRoom] getUserMedia error', err)
      setErrorMessage('Camera access is needed to record. Please allow camera permissions and try again.')
      setMode('error')
    }
  }

  function stopRecording() {
    if (!recorderRef.current) return
    if (recordingSeconds < MIN_VIDEO_SEC) return // guarded by UI anyway
    stopRecordingTimers()
    try { recorderRef.current.stop() } catch { /* noop */ }
  }

  // -- Multi-photo capture --------------------------------------------------

  function startMultiPhoto() {
    setErrorMessage(null)
    setPhotoQueue([])
    setMode('capturing_photos')
  }

  function triggerPhotoShutter() {
    if (photoQueue.length >= MULTI_PHOTO_MAX) return
    photoInputRef.current?.click()
  }

  async function handlePhotoFileSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (photoQueue.length >= MULTI_PHOTO_MAX) return
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      photoIdCounterRef.current += 1
      const id = `p-${photoIdCounterRef.current}`
      setPhotoQueue(prev => [...prev, {
        id,
        file: stripped,
        previewUrl: URL.createObjectURL(stripped),
      }])
    } catch (err) {
      console.error('[ScanRoom] photo preprocess failed', err)
      setErrorMessage(err?.message || 'Could not add that photo. Please try another.')
      setMode('error')
    }
  }

  function removePhoto(id) {
    setPhotoQueue(prev => {
      const removed = prev.find(p => p.id === id)
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter(p => p.id !== id)
    })
  }

  async function finishMultiPhoto() {
    if (photoQueue.length < MULTI_PHOTO_MIN) return
    setMode('extracting')
    setExtractTotal(photoQueue.length)
    setExtractProgress(0)
    try {
      const frames = []
      for (let i = 0; i < photoQueue.length; i++) {
        const compressed = await compressImage(photoQueue[i].file, 1600, 0.80)
        const dataUrl = await blobToDataUrl(compressed)
        frames.push(dataUrl.split(',')[1] || '')
        setExtractProgress(i + 1)
      }
      await postFramesToEdgeFunction(frames, 'multi_photo')
    } catch (err) {
      console.error('[ScanRoom] multi_photo pipeline failed', err)
      setErrorMessage(err?.message || 'Something went wrong preparing your photos.')
      setMode('error')
    }
  }

  // -- Frame extraction (video) --------------------------------------------

  async function extractAndProcess(blob) {
    setExtractProgress(0)
    setExtractTotal(0)
    try {
      const frames = await extractFrames(blob)
      await postFramesToEdgeFunction(frames, 'video')
    } catch (err) {
      console.error('[ScanRoom] video pipeline failed', err)
      setErrorMessage(err?.message || 'Something went wrong reading your video.')
      setMode('error')
    }
  }

  function extractFrames(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob)
      const video = document.createElement('video')
      video.src = url
      video.muted = true
      video.playsInline = true

      video.onloadedmetadata = async () => {
        try {
          const duration = isFinite(video.duration) && video.duration > 0 ? video.duration : MAX_VIDEO_SEC
          const startTime = SKIP_INITIAL_MS / 1000
          const numFrames = Math.max(1, Math.min(MAX_FRAMES, Math.floor((duration - startTime) / FRAME_INTERVAL_SEC) + 1))

          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth || 1280
          canvas.height = video.videoHeight || 720
          const ctx = canvas.getContext('2d')

          setExtractTotal(numFrames)

          const frames = []
          for (let i = 0; i < numFrames; i++) {
            const t = Math.min(duration - 0.05, startTime + (i * FRAME_INTERVAL_SEC))
            await seekVideo(video, t)
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
            frames.push(dataUrl.split(',')[1] || '')
            setExtractProgress(i + 1)
          }

          URL.revokeObjectURL(url)
          resolve(frames)
        } catch (err) {
          URL.revokeObjectURL(url)
          reject(err)
        }
      }

      video.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Could not decode the recorded video.'))
      }
    })
  }

  function seekVideo(video, t) {
    return new Promise((resolve) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked)
        resolve()
      }
      video.addEventListener('seeked', onSeeked)
      video.currentTime = t
    })
  }

  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result))
      r.onerror = () => reject(new Error('FileReader failed'))
      r.readAsDataURL(blob)
    })
  }

  // -- Edge Function call --------------------------------------------------

  async function postFramesToEdgeFunction(frames, captureMode) {
    setMode('uploading_frames')
    try {
      const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
        body: {
          capture_mode: captureMode,
          frames_base64: frames,
          frame_count: frames.length,
          room_id: room.id,
          home_id: homeId,
          media_type: 'image/jpeg',
          raw_video_pending: captureMode === 'video' && !!videoBlob,
        },
      })
      if (error) throw new Error(error.message || 'Edge function failed')
      if (!data || !data.session_id) throw new Error('No session_id returned')
      setScanResponse(data)
      setMode('review')
    } catch (err) {
      console.error('[ScanRoom] enumerate-room-items failed', err)
      setErrorMessage(err?.message || 'Something went wrong analyzing your scan.')
      setMode('error')
    }
  }

  // -- Renderers -----------------------------------------------------------

  function renderHeaderBar(title, hideClose = false) {
    return (
      <div style={headerStyle}>
        <div style={headerTitleStyle}>{title}</div>
        {!hideClose && (
          <button onClick={handleClose} aria-label="Close" style={closeBtnStyle}>
            <X size={22} color={C.muted} />
          </button>
        )}
      </div>
    )
  }

  function renderModeSelect() {
    return (
      <>
        {renderHeaderBar(`${room?.name || 'Room'} Photo`)}
        <div style={{ flex: 1, padding: '24px 18px', display: 'flex', flexDirection: 'column', gap: '14px', justifyContent: 'center' }}>
          <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, textAlign: 'center', marginBottom: '6px', lineHeight: 1.5 }}>
            Pick how you'd like to capture the room.
          </div>

          <button
            onClick={startRecording}
            style={tileButton(C.amber)}
          >
            <div style={tileIconCircle(C.amber)}>
              <VideoIcon size={28} color="#fff" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontFamily: f, fontSize: '17px', color: C.brown, fontWeight: '700' }}>Video walkthrough</div>
              <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginTop: '2px', lineHeight: 1.4 }}>
                Pan the room for 15–45 seconds. We'll sample frames and merge duplicates.
              </div>
            </div>
          </button>

          <button
            onClick={startMultiPhoto}
            style={tileButton(C.border)}
          >
            <div style={tileIconCircle(C.brown)}>
              <Camera size={28} color="#fff" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontFamily: f, fontSize: '17px', color: C.brown, fontWeight: '700' }}>Multi-photo</div>
              <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginTop: '2px', lineHeight: 1.4 }}>
                Snap up to {MULTI_PHOTO_MAX} angles. Best when the room is hard to capture in one shot.
              </div>
            </div>
          </button>
        </div>
      </>
    )
  }

  function renderRecording() {
    const canStop = recordingSeconds >= MIN_VIDEO_SEC
    const showWarning = recordingSeconds >= WARN_VIDEO_SEC
    return (
      <>
        {renderHeaderBar('Recording…', true)}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#000' }}>
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                background: '#000',
              }}
            />
            <div style={{
              position: 'absolute', top: '14px', left: '14px',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 12px', borderRadius: '999px',
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              fontFamily: fs, fontSize: '14px', fontWeight: '700',
            }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: C.danger }} />
              {formatMMSS(recordingSeconds)} / {formatMMSS(MAX_VIDEO_SEC)}
            </div>
            {showWarning && (
              <div style={{
                position: 'absolute', top: '14px', right: '14px',
                padding: '6px 12px', borderRadius: '999px',
                background: 'rgba(184,114,31,0.85)', color: '#fff',
                fontFamily: fs, fontSize: '12px', fontWeight: '700',
              }}>
                Almost at the {MAX_VIDEO_SEC}s limit
              </div>
            )}
            {!canStop && (
              <div style={{
                position: 'absolute', bottom: '110px', left: '50%', transform: 'translateX(-50%)',
                padding: '6px 12px', borderRadius: '999px',
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                fontFamily: fs, fontSize: '12px',
              }}>
                {MIN_VIDEO_SEC - recordingSeconds}s before you can stop
              </div>
            )}
          </div>
          <div style={{
            padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
            display: 'flex', justifyContent: 'center', background: '#000',
          }}>
            <button
              onClick={stopRecording}
              disabled={!canStop}
              aria-label="Stop recording"
              style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: canStop ? C.danger : '#3a3a3a',
                color: '#fff', border: '4px solid #fff', cursor: canStop ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: canStop ? 1 : 0.6,
              }}
            >
              <Square size={24} fill="#fff" color="#fff" />
            </button>
          </div>
        </div>
      </>
    )
  }

  function renderCapturingPhotos() {
    const canFinish = photoQueue.length >= MULTI_PHOTO_MIN
    const atCap = photoQueue.length >= MULTI_PHOTO_MAX
    return (
      <>
        {renderHeaderBar(`${room?.name || 'Room'} Photo`)}
        <div style={{ flex: 1, padding: '18px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <button
              onClick={() => { setPhotoQueue([]); setMode('mode_select') }}
              style={{
                background: 'transparent', color: C.muted, border: 'none',
                fontFamily: fs, fontSize: '13px', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted }}>
              {photoQueue.length} / {MULTI_PHOTO_MAX} photos
            </div>
          </div>

          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handlePhotoFileSelected}
          />

          {photoQueue.length === 0 ? (
            <div style={{
              padding: '40px 18px', textAlign: 'center', background: C.card,
              border: `1px dashed ${C.border}`, borderRadius: '14px',
              fontFamily: fs, fontSize: '13px', color: C.muted, lineHeight: 1.55,
            }}>
              Tap the shutter to capture each angle. You can add up to {MULTI_PHOTO_MAX} photos.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: '10px',
            }}>
              {photoQueue.map(p => (
                <div key={p.id} style={{ position: 'relative', aspectRatio: '1 / 1' }}>
                  <img
                    src={p.previewUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px', background: C.bg }}
                  />
                  <button
                    onClick={() => removePhoto(p.id)}
                    aria-label="Remove photo"
                    style={{
                      position: 'absolute', top: '-6px', right: '-6px',
                      width: '22px', height: '22px', borderRadius: '50%',
                      background: '#fff', border: `1px solid ${C.border}`,
                      cursor: 'pointer', padding: 0, lineHeight: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={12} color={C.muted} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{
          flexShrink: 0,
          padding: '12px 14px calc(18px + env(safe-area-inset-bottom))',
          borderTop: `1px solid ${C.border}`,
          background: C.card,
          display: 'flex', flexDirection: 'column', gap: '10px',
        }}>
          <button
            onClick={triggerPhotoShutter}
            disabled={atCap}
            style={{
              background: atCap ? C.border : 'transparent',
              color: atCap ? C.muted : C.brown,
              border: `1px solid ${C.border}`, borderRadius: '10px',
              padding: '12px', fontFamily: fs, fontSize: '14px', fontWeight: '600',
              cursor: atCap ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            <Camera size={16} strokeWidth={2} color={atCap ? C.muted : C.brown} />
            {photoQueue.length === 0 ? 'Take first photo' : atCap ? 'Limit reached' : 'Add another angle'}
          </button>
          <button
            onClick={finishMultiPhoto}
            disabled={!canFinish}
            style={{
              background: !canFinish ? C.border : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '14px', fontFamily: f, fontSize: '15px', fontWeight: '700',
              cursor: !canFinish ? 'not-allowed' : 'pointer',
              opacity: !canFinish ? 0.6 : 1,
            }}
          >
            {canFinish ? `Done — analyze ${photoQueue.length} photo${photoQueue.length === 1 ? '' : 's'}` : 'Take at least one photo'}
          </button>
        </div>
      </>
    )
  }

  function renderExtracting() {
    const pct = extractTotal > 0 ? Math.round((extractProgress / extractTotal) * 100) : 0
    return (
      <>
        {renderHeaderBar('Reading frames…', true)}
        <div style={centerPaneStyle}>
          <Spinner size={64} />
          <div style={{ fontFamily: f, fontSize: '20px', fontWeight: '700', color: C.brown, marginTop: '18px' }}>
            Reading frames…
          </div>
          <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, marginTop: '8px' }}>
            {extractTotal > 0 ? `${extractProgress} / ${extractTotal}` : 'Preparing'}
            {extractTotal > 0 && <span> · {pct}%</span>}
          </div>
        </div>
      </>
    )
  }

  function renderUploading() {
    return (
      <>
        {renderHeaderBar('Analyzing…', true)}
        <div style={centerPaneStyle}>
          <Spinner size={64} />
          <div style={{ fontFamily: f, fontSize: '20px', fontWeight: '700', color: C.brown, marginTop: '18px' }}>
            Analyzing your scan…
          </div>
          <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, marginTop: '8px', maxWidth: '320px', textAlign: 'center', lineHeight: 1.5 }}>
            Finding items and merging duplicates across frames. This usually takes 10–20 seconds.
          </div>
        </div>
      </>
    )
  }

  function renderError() {
    return (
      <>
        {renderHeaderBar(`${room?.name || 'Room'} Photo`)}
        <div style={centerPaneStyle}>
          <div style={{ fontFamily: f, fontSize: '20px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
            Something went wrong
          </div>
          <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, maxWidth: '340px', textAlign: 'center', lineHeight: 1.55, marginBottom: '22px' }}>
            {errorMessage || 'Please try again.'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { setErrorMessage(null); setMode('mode_select') }}
              style={{
                background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                color: '#fff', border: 'none', borderRadius: '10px',
                padding: '12px 22px', fontFamily: f, fontSize: '14px', fontWeight: '700', cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '10px',
                padding: '12px 22px', fontFamily: fs, fontSize: '14px', cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </>
    )
  }

  // ----- Render -----------------------------------------------------------

  if (mode === 'review' && scanResponse) {
    return (
      <RoomScan
        room={room}
        homeId={homeId}
        initialReview={scanResponse}
        onClose={onClose}
        onSaved={onSaved}
      />
    )
  }

  return (
    <div style={overlayStyle}>
      <style>{SPIN_STYLE}</style>
      {mode === 'mode_select' && renderModeSelect()}
      {mode === 'recording' && renderRecording()}
      {mode === 'capturing_photos' && renderCapturingPhotos()}
      {mode === 'extracting' && renderExtracting()}
      {mode === 'uploading_frames' && renderUploading()}
      {mode === 'error' && renderError()}
    </div>
  )
}

// ---- Inline style helpers (kept local for clarity) ------------------------

function tileButton(borderColor) {
  return {
    display: 'flex', alignItems: 'center', gap: '14px',
    background: '#fff', border: `1px solid ${borderColor}`,
    borderRadius: '14px', padding: '16px',
    cursor: 'pointer', textAlign: 'left',
    boxShadow: '0 1px 3px rgba(44,29,20,0.04)',
  }
}

function tileIconCircle(bg) {
  return {
    flexShrink: 0,
    width: '52px', height: '52px', borderRadius: '50%',
    background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }
}

const centerPaneStyle = {
  flex: 1, display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  padding: '24px 18px', textAlign: 'center',
}
